// helpers.ts — TS ports of the PHP slot-reader / label / icon utilities the
// list-display engine + driver closures depend on. Faithful mirrors of:
//   - layer_scoring_token_from_entry / item_arr_numeric_slot / item_arr_uuid_slot /
//     item_arr_scalar_layer_slot / list_display_layer_slot_string
//        (includes/functions/janus/items/layer_cell_helpers.php)
//   - ucwords_and_space / ucwords_and_space_with_icons (includes/functions.php)
//
// Reads go through the shared bucket-aware readers in ../shared/array-types.ts
// where the PHP went through layer_value()/layer_has(); the "top-level [0]" raw
// reads some driver closures use are mirrored verbatim by topSlot0() so the TS
// output matches the PHP byte-for-byte in compare mode.

import { layerOf, layerSlot, layerEntriesOf, itemsCol, layerValue, computedValue, plTotal, plPretty, plExact, primaryPictureOf, resolveNest } from '../shared/array-types';

export type ReferencedNames = Record<string, { item_name: string; item_type: string; pic_url?: string; icon?: string }>;

type UnknownRecord = Record<string, unknown>;
function recordOf(value: unknown): UnknownRecord | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as UnknownRecord
        : null;
}

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
export function isUuid(s: unknown): boolean {
    return typeof s === 'string' && UUID_RE.test(s);
}

// ── PHP layer_scoring_token_from_entry ──────────────────────────────────────
export function layerScoringToken(value: unknown): string | null {
    if (typeof value === 'string' || typeof value === 'number') {
        const s = String(value).trim();
        return s === '' ? null : s;
    }
    if (value && typeof value === 'object') {
        const o = value as Record<string, unknown>;
        if ('item_layer_effect' in o) {
            const s = String(o.item_layer_effect ?? '').trim();
            return s === '' ? null : s;
        }
        if (Array.isArray(value) && (typeof value[0] === 'string' || typeof value[0] === 'number')) {
            const s = String(value[0]).trim();
            return s === '' ? null : s;
        }
    }
    return null;
}

// ── PHP item_arr_numeric_slot ───────────────────────────────────────────────
export function numericSlot(cell: unknown, fallback = 0): number {
    if (typeof cell === 'number') return cell;
    if (typeof cell === 'string') {
        const t = cell.trim();
        return t !== '' && !isNaN(Number(t)) ? Number(t) : fallback;
    }
    if (cell && typeof cell === 'object') {
        const tok = layerScoringToken(cell);
        return tok !== null && !isNaN(Number(tok)) ? Number(tok) : fallback;
    }
    return fallback;
}

// ── PHP item_arr_uuid_slot ──────────────────────────────────────────────────
export function uuidSlot(cell: unknown): string {
    if (cell === null || cell === undefined || cell === '') return '';
    if (typeof cell === 'string') {
        const s = cell.trim();
        return isUuid(s) ? s : '';
    }
    if (cell && typeof cell === 'object') {
        const tok = layerScoringToken(cell);
        if (tok !== null && isUuid(tok)) return tok;
        // bucketed shell / rich entry
        const o = cell as {
            items?: { item_id?: unknown };
            item_id?: unknown;
        };
        const u = String(o.items?.item_id ?? o.item_id ?? '');
        const head = u.indexOf('|') !== -1 ? u.split('|', 2)[0]! : u;
        return isUuid(head) ? head : '';
    }
    return '';
}

// ── PHP item_arr_scalar_layer_slot ──────────────────────────────────────────
export function scalarLayerSlot(slot: unknown): string {
    if (slot === null || slot === undefined || slot === '') return '';
    if (Array.isArray(slot) || (slot && typeof slot === 'object')) {
        const id = uuidSlot(slot);
        if (id !== '') return id;
        return layerScoringToken(slot) ?? '';
    }
    if (typeof slot === 'string' || typeof slot === 'number') return String(slot);
    return '';
}

// ── PHP list_display_layer_slot_string ──────────────────────────────────────
export function slotString(slot: unknown): string {
    if (slot === null || slot === undefined || slot === false || slot === '') return '';
    if (Array.isArray(slot) || (slot && typeof slot === 'object')) {
        return layerScoringToken(slot) ?? '';
    }
    if (typeof slot === 'string' || typeof slot === 'number' || typeof slot === 'boolean') {
        return String(slot);
    }
    return '';
}

// ── duration-unit agreement ─────────────────────────────────────────────────
// The time rows (gathering / crafting / reading / training / spoilage) concatenate an int and a
// unit, which reads "1 days". Singularise the unit when the count is exactly 1.
// Units come from the duration_type vocabulary — seconds / rounds / minutes / hours / days /
// weeks / months / years — all of which pluralise by a trailing 's'. 'permanent' carries no count
// and has no trailing 's', so it falls through untouched, as does anything already singular.
// Tidy a duration COUNT for display: drop trailing zeros from a decimal so "1.50" reads "1.5"
// and "1.0000000000" reads "1". Non-numeric or already-clean values pass through untouched, so
// this can never mangle an unexpected value into something wrong.
//
// NOTE (verified 2026-07-20): live *_int time layers all store clean integer strings — the
// decimals live in `layers.qty`, a DECIMAL column these rows do not read. So this is robustness
// for a legitimately fractional authored value (e.g. 1.5 hours), not a fix for existing data.
export function formatDurationCount(count: unknown): string {
    const s = String(count ?? '').trim();
    if (s === '' || !/^-?\d+\.\d+$/.test(s)) return s;
    return s.replace(/\.?0+$/, '');
}

export function durationUnitForCount(count: unknown, unit: string): string {
    const u = String(unit ?? '').trim();
    if (u === '' || !u.endsWith('s')) return u;
    const n = Number(String(count ?? '').trim());
    return Number.isFinite(n) && Math.abs(n) === 1 ? u.slice(0, -1) : u;
}

// ── PHP sort_items_by_requirements ──────────────────────────────────────────
// IMPORTANT: in PHP this is a RENDER-TIME mutation (includes/functions.php
// sort_items_by_requirements), NOT something baked into the cached blob — the
// blob stores ability / spell-known entries in authoring order. So the TS port
// MUST replicate the sort here; "order comes from the blob" was wrong.
//
// Sorts ascending by the HIGHEST applicable requirement of each entry:
// requires_level, requires_class_level (gated in-data by requires_class/_subclass),
// or skill-tier level. Entries with no requirement sort to 0 (front). This is the
// READ path (show_user_select_dropdowns === false), so the class-level dropdown skip
// in the PHP is not replicated. Stable (matches PHP 8 asort) so equal requirements
// keep authoring order.
//
// `root` is the top-level blob (ctx.arr). On a class/subclass page (and any page that
// ships ability lists as bare-UUID refs) the entries are plain UUID STRINGS with no
// item_layer_types, so reading requires_class_level off the raw entry always yielded 0
// and the AbilityType lists rendered in authoring order, NOT class-level order. PHP's
// sort_items_by_requirements re-queries each entry via query_full_item_arr to read the
// requirement; the client can't, so resolve the bare ref to its FULL in-blob def
// (root .catalog / nested full copy) via resolveNest before reading. Omit `root` to keep
// the legacy raw-entry behavior (callers that already pass full-nest entries).
//
// `resolve` (optional) overrides the entry→full-node resolution. The default resolveNest
// only sees `root` (ctx.arr); some lists (e.g. override_ability in the side-info mount) ship
// their referenced items as identity SHELLS in the blob, with the full def — carrying the
// requires_skill_tier_N layer — reachable only via the row renderer's resolver (ctx.fullById +
// the global ftFindFullNestedItem). Passing that resolver here makes the sort read the SAME
// node the badges do, so it can't silently no-op when the full def lives outside root.
export function sortLayerEntriesByRequirements<T>(entries: T[], root?: unknown, resolve?: (entry: T) => unknown, classLevel?: number): T[] {
    if (!Array.isArray(entries)) return entries;
    if (classLevel !== undefined) entries = entries.filter(entry =>
        Number(layerOf(resolve ? resolve(entry) : resolveNest(entry, root), 'requires_class_level') ?? 0) <= classLevel);
    if (entries.length < 2) return entries;

    // requires_skill_tier_<key> → skill_tier_level (from inlined SKILLTIER_CATALOG).
    const tierLevelByKey: Record<string, number> = {};
    if (typeof SKILLTIER_CATALOG !== 'undefined' && SKILLTIER_CATALOG) {
        for (const tier of Object.values(SKILLTIER_CATALOG)) {
            const key = tier && tier.requires_skill_tier;
            if (key) tierLevelByKey[key] = Number(tier.skill_tier_level) || 0;
        }
    }

    const sortValueOf = (entry: T): number => {
        // Resolve a bare-UUID / shell ability ref to its full in-blob def so the
        // requirement layers are readable (mirrors PHP's per-entry query_full_item_arr).
        // Full-nest entries and the no-root path pass through unchanged.
        const node: unknown = resolve
            ? resolve(entry)
            : ((root !== undefined && root !== null) ? resolveNest(entry, root) : entry);
        // entries are full-nested bucketed item_arrs post-reshape; read bucket-aware.
        let sortValue: number | null = null;

        const rl = layerOf<string | number>(node, 'requires_level');
        if (rl !== undefined && rl !== null && rl !== '') sortValue = Math.trunc(Number(rl)) || 0;

        // Read path applies requires_class_level unconditionally (no dropdown skip).
        const rcl = layerOf<string | number>(node, 'requires_class_level');
        if (rcl !== undefined && rcl !== null && rcl !== '') {
            const n = Math.trunc(Number(rcl)) || 0;
            if (sortValue === null || n > sortValue) sortValue = n;
        }

        for (const key in tierLevelByKey) {
            if (layerEntriesOf(node, key).length > 0) {
                const lvl = tierLevelByKey[key]!;
                if (sortValue === null || lvl > sortValue) sortValue = lvl;
            }
        }

        return sortValue ?? 0;
    };

    // Decorate-sort-undecorate keeps the sort stable across engines.
    return entries
        .map((entry, index) => ({ entry, index, sortValue: sortValueOf(entry) }))
        .sort((a, b) => (a.sortValue - b.sortValue) || (a.index - b.index))
        .map(x => x.entry);
}

// ── PHP ucwords_and_space ───────────────────────────────────────────────────
const LOWERCASE_WORDS = new Set([
    'a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'of', 'on', 'at', 'to', 'from', 'by', 'in', 'with',
]);
function ucfirst(w: string): string {
    return w === '' ? '' : w.charAt(0).toUpperCase() + w.slice(1);
}
export function ucwordsAndSpaceTs(str: unknown): string {
    if (str === null || str === undefined) return '';
    const lowered = String(str).trim().toLowerCase().replace(/_/g, ' ');
    const words = lowered.split(' ');
    const out = words.map((w, i) => (i === 0 || !LOWERCASE_WORDS.has(w)) ? ucfirst(w) : w);
    return out.filter(w => w !== '').join(' ');
}

// ── PHP ucwords_and_space_with_icons ────────────────────────────────────────
// Each underscore-delimited word resolves to an icon; if iconMap returns the
// help-circle-outline default, fall back to the Title-cased word instead.
export function ucwordsAndSpaceWithIcons(str: string): string {
    const words = String(str).split('_');
    let out = '';
    for (const word of words) {
        const iconHtml = iconMap(word);
        if (!_isUnknownIcon(iconHtml)) {
            out += iconHtml + ' ';
        } else {
            out += word.replace(/\b\w/g, c => c.toUpperCase()) + ' ';
        }
    }
    return out.trim();
}

// ── header icon resolution (render_list_display_single_item rules) ───────────
// icon === false/'' → none; null/undefined → iconMap(layer); string w/ '<' → raw HTML;
// other string → iconMap(icon).
export function headerIconHtml(iconOpt: string | false | null | undefined, layerName: string): string {
    if (iconOpt === false || iconOpt === '') return '';
    if (iconOpt === null || iconOpt === undefined) return iconMap(layerName) ?? '';
    if (typeof iconOpt === 'string' && iconOpt.indexOf('<') !== -1) return iconOpt;
    return iconMap(iconOpt) ?? '';
}

// icon for an item instance id, falling back to its item_type icon when the id has no
// catalog icon (mirrors PHP icon_map(uuid) → type-icon behavior). Used for icon-before-name
// item types like creature_template / language whose per-item UUID isn't a catalog icon.
export function iconMapOrType(id: string, type: string): string {
    const ico = iconMap(id);
    if (_isUnknownIcon(ico) && type) {
        return iconMap(type);
    }
    return ico;
}

// Resolve an icon for a referenced item id, mirroring PHP icon_map(uuid): try the item's
// variable_name (catalog skills/status effects/etc. — ICON_LOOKUP is keyed by variable_name),
// then the raw id (NamedIcon UUIDs), then the item_type icon (creature_template instances etc.).
// True when iconMap() RENDERED the help-circle-outline fallback (couldn't resolve the key). Inspect the
// actual <img src="…">, NOT a bare substring — every iconMap() output also carries help-circle-outline in
// its onerror="this.src='…help-circle-outline.png'" handler, so a substring check is ALWAYS true and would
// discard correctly-resolved icons (e.g. registry-resolved catalog UUIDs), wrongly using the type icon.
function _isUnknownIcon(ico: string): boolean {
    if (typeof ico !== 'string' || ico === '') return true;
    const m = ico.match(/\ssrc="([^"]*)"/); // the real icon src (onerror uses this.src='…', single-quoted)
    // No <img src> → iconMap() returned a rune TYPE-BADGE (<svg> span) or its deferred ft-icon
    // placeholder for an ability-type key — a resolved icon, NOT help-circle. Treat as known so
    // ucwordsAndSpaceWithIcons / iconMapOrType / iconForRef keep the badge instead of falling back.
    if (!m) return false;
    return m[1]?.indexOf('help-circle-outline') !== -1;
}
/**
 * True when a referenced id has its OWN icon — a server-resolved `icon` on the names map, or a
 * registry entry keyed by the UUID (creature types, environments, weather, light types, spheres,
 * weapon types … every NamedIcon catalog). No item_type fallback: an arbitrary item that only has a
 * generic type glyph does not count. Gates the list-row icon-before-name for types outside
 * ICON_BEFORE_NAME_ITEM_TYPES (the "Ability only works against creature type: Undead" rows).
 */
export function refHasOwnIcon(names: ReferencedNames, id: string): boolean {
    if (!id) return false;
    const ref = names ? (names[id] ?? names[id.toUpperCase()]) : undefined;
    if (ref?.icon) return true;
    return !_isUnknownIcon(iconMap(id));
}

/** iconMap(key) + trailing space when the key has a registered icon, else '' (never a "?" glyph). */
export function iconIfKnown(key: string): string {
    if (!key) return '';
    const ico = iconMap(key);
    return _isUnknownIcon(ico) ? '' : ico + ' ';
}

export function iconForRef(names: ReferencedNames, id: string, type = ''): string {
    const ref = names ? (names[id] ?? names[(id ?? '').toUpperCase()]) : undefined;
    // Server-resolved icon URL (icon_map) for catalog items whose UUID isn't in ICON_LOOKUP.
    if (ref?.icon) {
        return '<img src="' + escapeHtml(ref.icon) + '" width="25" height="25" alt="" draggable="false" style="pointer-events: none;">';
    }
    const byId = iconMap(id);
    if (!_isUnknownIcon(byId)) return byId;
    return type ? iconMap(type) : byId;
}

// ── name resolution for bare-UUID references (referenced-names map) ──────────
// Mirror of PHP get_item_id_name() for the read-display path: shells carry their
// own name; bare UUIDs resolve via the _ft_referenced_names map embedded by
// get_item_arr.php.
export function nameOf(names: ReferencedNames, id: string): string {
    if (!id) return '';
    const hit = names[id] ?? names[id.toUpperCase()];
    if (hit && hit.item_name) return hit.item_name;
    // Not in the server _ft_referenced_names map (e.g. a catalogized armor_classification /
    // composition whose def lives in the ENCOUNTER-root catalog, not nested in this blob). Resolve
    // from the session catalog (ftItemNameById, memoized, walks every cache root's .catalog) before
    // giving up — otherwise the name link renders blank (the natural-armor "Natural Armors" /
    // "Flesh" rows). Keep '' on a true miss (callers degrade to icon-only).
    if (typeof window.ftItemNameById === 'function') {
        const nm = window.ftItemNameById(id);
        if (nm) return String(nm);
    }
    return '';
}

export function unresolvedReferenceHtml(layerName: string, id: string): string {
    return '<span class="ft-ld-row-unresolved text-secondary fst-italic">'
        + escapeHtml(ucwordsAndSpaceTs(layerName)) + ' &middot; unresolved reference '
        + '<code class="small">' + escapeHtml(id) + '</code></span>';
}

/** A named reference can be text-only, a delegated link, or an ordinary anchor.
 * Keep its identity in the DOM so deferred catalog reads cover all three. */
export function referenceNameHtml(names: ReferencedNames, id: string, ownName = ''): string {
    const name = ownName || nameOf(names, id);
    if (!isUuid(id)) return escapeHtml(name);
    return '<span data-ft-reference-id="' + escapeHtml(id) + '">'
        + (name ? escapeHtml(name) : unresolvedReferenceHtml('reference', id)) + '</span>';
}

// ── blob `.catalog` → names (the PHP ft_catalog_shell() name source) ─────────
// The clean blob ships an in-place `.catalog` of the shells it references (keyed by
// UUID). Some bare-UUID refs — e.g. status_effect ids in the status-effect-parameter
// blocks — are NOT in the separate _ft_referenced_names map, so nameOf() alone returns
// '' (icon resolves via ICON_LOOKUP but the name link goes blank). We read the name
// straight off the blob catalog instead of a server get_item_id_name() round-trip.
// Catalog entries come in two shapes: flat `{item_name,item_type}` and full shell
// `{items:{item_name,...},item_layer_types,...}`.
export function namesFromCatalog(arr: unknown): ReferencedNames {
    const out: ReferencedNames = {};
    const cat = recordOf(recordOf(arr)?.catalog);
    if (!cat) return out;
    for (const key in cat) {
        const e = recordOf(cat[key]);
        if (!e) continue;
        const items = recordOf(e.items);
        const name = String(e.item_name ?? items?.item_name ?? '');
        if (name === '') continue;
        const type = String(e.item_type ?? items?.item_type ?? '');
        out[key.toUpperCase()] = { item_name: name, item_type: type };
    }
    return out;
}

// ── stripped static-registry refs → names (DAMAGETYPE / ROLLTYPE / SKILLTIER / SKILL) ─────────
// The object-API producer DROPS damage_type / roll_type / skill / skill_tier refs from the blob
// catalog + _ft_referenced_names map and ships them as BARE UUIDs (shape v4/v5) — they resolve via
// these page-global static registries, NOT a per-item DB row. PHP's render_card_list_item_layer
// re-queries the item by UUID for the name/type; the TS render can't, so a damage_type_resistance /
// damage_type_immunity / triggers_on_roll_type / etc. row landed with name '' AND type '' → no
// icon-before-name and an empty <li> (the "Damage Type Resistance:" / "Triggers after a roll type"
// header with a blank body — the exact PHP-correct / sideInfo-wrong divergence the user reported).
// Status effects render fine only because the producer DOES intern those into the blob catalog.
// Fold the registries into the names map (keyed by each entry's item_id) so nameOf() + the
// `ctx.names[id]?.item_type` reads resolve them like any blob-catalog ref. Lowest precedence — the
// blob catalog + _ft_referenced_names win, so an authored override is never shadowed.
function namesFromStaticRegistries(): ReferencedNames {
    const out: ReferencedNames = {};
    const w = (typeof window !== 'undefined') ? window as unknown as {
        DAMAGETYPE_CATALOG?: unknown;
        ROLLTYPE_CATALOG?: unknown;
        SKILLTIER_CATALOG?: unknown;
        SKILL_CATALOG?: unknown;
    } : null;
    if (!w) return out;
    const add = (catalog: unknown, itemType: string) => {
        const cat = recordOf(catalog);
        if (!cat) return;
        for (const k of Object.keys(cat)) {
            const e = recordOf(cat[k]);
            if (!e) continue;
            const id = String((e.item_id ?? k) || '');
            if (!isUuid(id)) continue;
            const name = String(e.item_name ?? e.name ?? '');
            if (name === '') continue;
            const key = id.toUpperCase();
            // First writer wins; a registry that carries the item_type stamps it (drives
            // ICON_BEFORE_NAME so the per-row icon renders before the name, matching PHP).
            if (out[key] === undefined) out[key] = { item_name: name, item_type: itemType };
        }
    };
    add(w.DAMAGETYPE_CATALOG, 'damage_type');
    add(w.ROLLTYPE_CATALOG, 'roll_type');
    add(w.SKILLTIER_CATALOG, 'skill_tier');
    add(w.SKILL_CATALOG, ''); // skill item_type varies per skill; name-only fill (better than blank)
    return out;
}

// ── `ability_choice_options` → names (the server's OWN id→name list for this layer) ─────────
// An `ability_choice` layer ships its targets as BARE UUIDs, and the option DEFS are not always
// reachable from the node being rendered: on the cache-render path (openItemInfo → render-from-
// cache) the resolved node is frequently a root-catalog DEF with no `.catalog` of its own, so
// nameOf() had nothing to resolve against and every choice row painted as a COMPLETELY EMPTY
// <li> — the "Ability choices are blank in the side info" report. The server already solves this
// exact problem for itself: ensure_ability_choice_options_on_ability_arr() (functions.php) stamps
// `ability_choice_options` = [{item_id, item_name}, ...] onto every ability/spell array carrying
// the layer, and the PC-sheet ability rows read it. Harvest it here too, so the list display
// resolves choice names from the SAME in-blob source with zero network and zero dependence on a
// warm ambient cache. Deliberately SHALLOW — root + each root-catalog def, never a deep blob walk —
// mirroring ft_object_api_bake_monster_template_names()'s hot-path discipline. That covers the
// rendered root and the catalog-as-store defs, which is where the server stamps the key; a host
// buried deeper (a full-nest provider node) is not scanned, and falls back to the catalog / names
// map as before.
export function namesFromAbilityChoiceOptions(arr: unknown): ReferencedNames {
    const out: ReferencedNames = {};
    const harvest = (node: unknown): void => {
        const opts = recordOf(node)?.ability_choice_options;
        if (!Array.isArray(opts)) return;
        for (const o of opts) {
            const entry = recordOf(o);
            if (!entry) continue;
            const id = String(entry.item_id ?? '');
            const name = String(entry.item_name ?? '');
            if (id === '' || name === '') continue;
            out[id.toUpperCase()] ??= { item_name: name, item_type: String(entry.item_type ?? '') };
        }
    };
    const root = recordOf(arr);
    if (!root) return out;
    harvest(root);
    const cat = recordOf(root.catalog);
    if (cat) for (const key in cat) harvest(cat[key]);
    return out;
}

// _ft_referenced_names overlaid on the catalog-derived names — referenced-names WINS
// (it's the authoritative get_item_id_name() mirror); the catalog fills gaps the map omits, the
// blob's own `ability_choice_options` fills the choice-target gap neither carries on a lean
// cache-rendered node, and the page-global static registries (damage_type / roll_type / skill /
// skill_tier — stripped to bare UUIDs by the producer) backstop the rest. Used once per top-level
// render to enrich ctx.names so every nameOf() call transparently resolves catalog- and
// registry-only ids.
export function mergeNamesWithCatalog(names: ReferencedNames, arr: unknown): ReferencedNames {
    return {
        ...namesFromStaticRegistries(),
        ...namesFromAbilityChoiceOptions(arr),
        ...namesFromCatalog(arr),
        ...(names || {}),
    };
}

// ── label separator (render_list_display_single_item $list_display_after_label_sep) ──
export function afterLabelSep(label: string, labelHtml: boolean): string {
    if (labelHtml) return ' ';
    return label.replace(/\s+$/, '').endsWith(':') ? ' ' : ': ';
}

// ── PHP layer_has (presence, bucket-/carrier-aware) ─────────────────────────
export function hasLayer(arr: unknown, layerName: string): boolean {
    if (arr === null || arr === undefined) return false;
    const raw = layerSlot(arr, layerName);
    if (raw !== null && raw !== undefined && raw !== '') {
        if (Array.isArray(raw)) {
            if (raw.length > 0) return true;
        } else {
            return true;
        }
    }
    // carrier walk (layerOf / layerEntriesOf both descend into default-provider carriers)
    if (layerOf(arr, layerName) !== undefined) return true;
    return layerEntriesOf(arr, layerName).length > 0;
}

// PHP `isset($arr[$name])` after the bucket-reshape: get_item_arr.php returns a PURE-BUCKET
// blob (layers under item_layer_types), so a layer's presence must be checked BUCKET-FIRST.
// (The PHP combined-list sees layers at top level only on paths that splice item_layer_types
// up — e.g. monster_template — which is exactly why a creature_template's bucketed v_dice /
// spell_prepared / etc. were invisible to a top-level-only check here.) Legacy top-level
// fallback kept for any non-bucketed/legacy shape.
function _slotOf(arr: unknown, name: string): unknown {
    const root = recordOf(arr);
    if (!root) return undefined;
    const ilt = recordOf(root.item_layer_types);
    if (ilt && ilt[name] !== undefined) return ilt[name];
    return root[name];
}
export function hasTopLevel(arr: unknown, name: string): boolean {
    const v = _slotOf(arr, name);
    return v !== null && v !== undefined;
}

// PHP `!empty($arr[$name])` — bucket-first, excluding falsy/empty-array.
export function notEmptyTopLevel(arr: unknown, name: string): boolean {
    const v = _slotOf(arr, name);
    if (v === null || v === undefined) return false;
    if (Array.isArray(v)) return v.length > 0;
    return v !== '' && v !== 0 && v !== '0' && v !== false;
}

// First raw slot — bucket-first, mirroring PHP `$arr[$layer][0] ?? ''` against the spliced blob.
export function topSlot0(arr: unknown, layerName: string): unknown {
    const slot = _slotOf(arr, layerName);
    if (slot === null || slot === undefined) return '';
    if (Array.isArray(slot)) return slot[0] ?? '';
    return slot;
}

// ── CardListItemLayer constants (additions.php) — stable, hardcoded like array-types' DEFAULT_PROVIDER_LAYERS ──
export const HIDE_REQUIRES_LEVEL = ['at_will_spell', 'spell_known', 'curse_known', 'innate_spell_per_long_rest'];
export const ICON_BEFORE_NAME_ITEM_TYPES = [
    'innate_skill', 'gathering_skill', 'knowledge_skill', 'crafting_skill', 'language_skill',
    'instrument_classification_skill', 'weapon_skill', 'armor_skill', 'defensive_skill',
    'damage_type', 'status_effect', 'skill_tier', 'language', 'weapon_classification', 'roll_type',
    'creature_template',
    // Taxonomy references (2026-09-16): not every one has a per-UUID icon (sub-types / lineages /
    // ancestries / groupings aren't NamedIcon catalogs), so iconForRef falls back to the type glyph.
    'creature_type', 'creature_sub_type', 'lineage', 'creature_lineage', 'ancestry', 'creature_ancestry',
    'creature_grouping', 'environment', 'weather', 'light_type', 'armor_type', 'weapon_type',
    'weapon_category', 'sphere_of_magic', 'spell_circle', 'terrain_type', 'lock_type', 'speed_type',
    'summon_type',
];

// ── PHP CardListItemLayer::formatQty ────────────────────────────────────────
export function formatQty(qty: unknown): string {
    const n = Number(qty);
    if (qty === null || qty === undefined || qty === '' || isNaN(n)) return String(qty ?? '');
    return n >= 1 ? String(Math.round(n)) : String(Math.round(n * 100) / 100);
}

// PHP display_spell_casting_time — now sourced from the shared single-source module so the
// side-info card, the spell list row, and the PC sheet can't drift. Re-exported here to keep
// the existing `import { displaySpellCastingTime } from './helpers'` call sites (primitives.ts) intact.
export { displaySpellCastingTime } from '../shared/spell-casting-time';

// ── PHP show_requires_class_level_icons (echoes; here returns the HTML) ──────
export function showRequiresClassLevelIcons(arr: unknown, iconW = 25, iconH = 25): string {
    const lvl = layerOf<string>(arr, 'requires_class_level');
    const cls = layerOf<string>(arr, 'requires_class');
    const sub = layerOf<string>(arr, 'requires_subclass');
    if (!lvl) return '';
    if (sub) {
        return '<span class="position-relative" role="button" onclick="openItemInfo(\'' + escapeHtml(sub) + '\');">'
            + '<span class="position-absolute top-0 start-100 translate-middle badge border rounded-pill bg-success">' + escapeHtml(lvl) + '</span>'
            + iconMap(sub, iconW, iconH) + '</span>';
    }
    if (cls) {
        return '<span class="position-relative" role="button" onclick="openItemInfo(\'' + escapeHtml(cls) + '\');">'
            + '<span class="position-absolute top-0 start-100 translate-middle badge border rounded-pill bg-success">' + escapeHtml(lvl) + '</span>'
            + iconMap(cls, iconW, iconH) + '</span>';
    }
    return '';
}

// ── skill-tier badges (requires_skill_tier_N → icon + skill icon) ───────────
export function skillTierBadges(arr: unknown): string {
    let out = '';
    for (const meta of Object.values(SKILLTIER_CATALOG)) {
        const tierKey = meta.requires_skill_tier;
        if (!tierKey || !hasLayer(arr, tierKey)) continue;
        for (const entry of layerEntriesOf(arr, tierKey)) {
            const skillId = (typeof entry === 'string')
                ? (entry.indexOf('|') !== -1 ? entry.split('|', 2)[0]! : entry)
                : scalarLayerSlot(entry);
            if (!skillId) continue;
            out += '<span class="badge bg-danger ml-1">' + iconMap(tierKey) + ' ' + iconMap(skillId) + '</span>';
        }
    }
    return out;
}

// Re-export the shared bucket readers under names that read like the PHP for the driver.
export { layerOf, layerSlot, layerEntriesOf, itemsCol, layerValue, computedValue, plTotal, plPretty, plExact, primaryPictureOf };
